import {
  TriggerConfigs,
  TriggerForEditor,
  TriggerStepCreate,
  When,
} from "@dots-projects/types";
import { Thunk } from "./useThunkReducer";
import {
  TriggerEditorAction,
  TriggerEditorState,
  MutateTriggerAction,
  PersistKind,
  OnPersistTrigger,
  FlowAction,
} from "./reducerTypes";
import { message } from "antd";
import { PlaceholderData } from "./stateUtils";

/*** Action creators *******/
/*

Following Redux convention, here are some action creators. We can use them like so:

```ts
dispatch(selectNode('node-id'))
```


In fact, we don't always need to create an action creator for every action. TypeScript makes things very easy to write simple actions in place:

```ts
dispatch({type: 'unselect'})
```
*/

export const selectNode = (nodeId: string): TriggerEditorAction => ({
  type: "select_node",
  payload: { nodeId },
});

export const selectEdge = (edgeId: string): TriggerEditorAction => ({
  type: "select_edge",
  payload: { edgeId },
});

export const selectTransition = (
  stepId: string,
  targetStepId: string,
  whenAsString?: string
): TriggerEditorAction => ({
  type: "select_transition",
  payload: { stepId, targetStepId, whenAsString },
});

export const addFloatingPlaceholder = (
  id: string,
  data: Omit<PlaceholderData, "isFloating">,
  position: { x: number; y: number }
): TriggerEditorAction => ({
  type: "add_floating_placeholder",
  payload: { id, data: { ...data, isFloating: true }, position },
});

export const removeNode = (nodeId: string): TriggerEditorAction => ({
  type: "remove_node",
  payload: { id: nodeId },
});

export const updateStep = (step: TriggerStepCreate): MutateTriggerAction => ({
  type: "update_step",
  payload: { step },
});

export const updateTriggerName = (name: string): MutateTriggerAction => ({
  type: "update_trigger_name",
  payload: { name },
});

export function addSteps(
  steps: TriggerStepCreate[],
  data?: {
    transitionIndex?: number;
    parentStepId?: string;
    isParentTriggerRoot?: boolean;
    when?: When;
    relayout?: boolean;
  }
): MutateTriggerAction {
  const { parentStepId, transitionIndex, when, isParentTriggerRoot } =
    data ?? {};
  return {
    type: "add_steps",
    payload: {
      steps,
      parentStepId,
      isParentTriggerRoot,
      when,
      transitionIndex,
      relayout: data?.relayout ?? true,
    },
  };
}

export const deleteSteps = (stepIds: string[]): MutateTriggerAction => ({
  type: "delete_steps",
  payload: { stepIds },
});

export const updateButtonClickTriggerConfig =
  (updateButtonClickTriggerConfiguration: {
    message: string;
    htmlMessage: string;
    buttonText: string;
    buttonEmoji: string;
    channelId: string;
  }): MutateTriggerAction => {
    return {
      type: "update_button_click_trigger_config",
      payload: {
        ...updateButtonClickTriggerConfiguration,
      },
    };
  };

export const updateRoleChangeTriggerConfig =
  (updateRoleChangeTriggerConfiguration: {
    newTrigger: TriggerForEditor;
  }): MutateTriggerAction => {
    return {
      type: "update_role_change_trigger_config",
      payload: {
        ...updateRoleChangeTriggerConfiguration,
      },
    };
  };

export const updateTriggerConfig = (
  draftConfig: TriggerConfigs,
  liveTriggerConfigs: TriggerConfigs
): MutateTriggerAction => {
  return {
    type: "update_trigger_config",
    payload: {
      draftConfig,
      liveTriggerConfigs,
    },
  };
};

export const setTriggerTypeError = (): FlowAction => {
  return {
    type: "set_trigger_type_error",
  };
};

export const setStepError = (stepId: string): FlowAction => {
  return {
    type: "set_step_error",
    payload: {
      stepId,
    },
  };
};

export const hideNodeErrorBorder = (): FlowAction => {
  return {
    type: "hide_node_error_border",
  };
};

/**
 * "Thunk" is a Redux concept. It can be used to dispatch actions after an async operation is done. https://redux.js.org/usage/writing-logic-thunks
 */
type PersistThunk = Thunk<TriggerEditorAction, TriggerEditorState>;

let persistDebounceTimeout: NodeJS.Timeout | undefined = undefined;
let persistDelayTimeout: NodeJS.Timeout | undefined = undefined;
/**
 * This is the common code extracted from `saveDraft`, `createDraft` and `publish`. It creates a "PersistThunk creator" so that we could dispatch a thunk to perform an async operation and dispatch some actions.
 *
 * ```ts
 * dispatch(saveDraft(onPersist))
 * ```
 *
 * @param kind
 * @returns
 */
const createPersistThunkCreator =
  (kind: PersistKind) =>
  (onPersist: OnPersistTrigger): PersistThunk =>
  async (dispatch, getState) => {
    async function performPersist() {
      //Clean errors
      dispatch(hideNodeErrorBorder());
      try {
        const { trigger } = getState();
        if (kind === "view-draft" || kind === "publish") {
          dispatch({ type: "unselect" });
        }
        dispatch({ type: "$$update_is_persisting", payload: kind });
        const [persistedTrigger, persistResponse] = await onPersist(
          kind,
          trigger.current
        );
        dispatch({ type: "$$update_is_persisting", payload: false });
        dispatch({
          type: "$$update_persist_response",
          payload: { kind, response: persistResponse },
        });
        // If there is a delayed persist action, we don't replace the current trigger. This is to prevent an outdated trigger from appearing in the UI. When the delayed persist action finishes, we'll dispatch `$$replace_current_trigger` to properly update the UI state.
        if (!persistDelayTimeout && !persistDebounceTimeout) {
          dispatch({
            type: "$$replace_current_trigger",
            payload: {
              trigger: persistedTrigger,
              clearHistory: kind === "view-draft" || kind === "publish",
            },
          });
        }
      } catch (err: any) {
        message.error(
          `${
            err.message
              ? err.message.replaceAll(/\\|\"/g, "")
              : "An error occurred, please contact support."
          }`,
          5
        );
        window.analytics.track("Error caught", JSON.stringify(err));
      }
    }

    function tryPersist() {
      const { editor } = getState();
      if (editor.isPersisting) {
        if (kind === "save-draft") {
          // If the editor is in the process of persisting, we'll try again later
          if (persistDelayTimeout) clearTimeout(persistDelayTimeout);
          persistDelayTimeout = setTimeout(tryPersist, 1000);
        } else {
          // discard the action, publish and create-draft UIs are supposed to be disabled while isPersisting is true.
        }
      } else {
        performPersist();
        persistDelayTimeout = undefined;
      }
    }

    // debounce save-draft operations: If there are many save-draft actions being dispatched in a short time (within 500 msecs), we only dispatch the last one.
    if (kind === "save-draft") {
      if (persistDebounceTimeout) clearTimeout(persistDebounceTimeout);
      persistDebounceTimeout = setTimeout(tryPersist, 500);
    } else {
      tryPersist();
    }
  };

export const saveDraft = createPersistThunkCreator("save-draft");

export const viewDraft = createPersistThunkCreator("view-draft");

export const publish = createPersistThunkCreator("publish");

export const tryDraft = createPersistThunkCreator("try-draft");
